import { a3Copy } from "@/components/assignments/a3-copy";
import { useContext, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BentoVisit } from "@/components/ui/box-visit-context";
import { teachingDraftsApi, type TeachingDraftHeader } from "@/lib/api/teachingDrafts";
import { Button } from "@/components/ui/button";
import { RecordTable } from "@/components/records/RecordTable";
import { useAuth } from "@/contexts/AuthContext";
import { useTeamNames } from "./use-team-names";
import { draftRelation, draftSaved } from "./coursework-history";
import { teachingDraftsKey } from "./teaching-draft-model";


/**
 * The teaching team's private writing for this course. Unfinished drafts lead,
 * each with its purpose and who saved it last; retained history (discarded or
 * published drafts) stays readable behind its own named disclosure, so it
 * never reads as work still to do.
 */
export function TeachingDraftList({ courseId, box, scope, timezone }: { courseId: string; box: string; scope: string; timezone: string }) {
  const { me } = useAuth();
  const name = useTeamNames(courseId, true, me?.user_id);
  const host = useContext(BentoVisit);
  const [offset, setOffset] = useState(0);
  const read = useQuery({ queryKey: teachingDraftsKey(courseId, scope, offset), queryFn: () => teachingDraftsApi.list(courseId, offset), retry: false });
  if (read.isError) return <section aria-label={a3Copy.author.teachingDrafts}><p>{a3Copy.author.yourTeachingDraftsCouldNotBeRead}<Button type="button" variant="link" onClick={() => void read.refetch()}>{a3Copy.author.tryAgain}</Button></p></section>;
  if (!read.data) return <p role="status">{a3Copy.author.readingTeachingDrafts}</p>;
  if (!read.data.items.length && offset === 0) return null;
  const active = read.data.items.filter(row => row.state === "active");
  const history = read.data.items.filter(row => row.state !== "active");
  const stateWord = (row: TeachingDraftHeader) => row.state === "active" ? a3Copy.author.privateDraft : row.state === "discarded" ? a3Copy.author.discarded : a3Copy.author.published;
  const table = (rows: TeachingDraftHeader[], label: string, empty: string) => <div role="group" aria-label={label}><RecordTable
    rows={rows} rowId={row => row.id}
    primary={{ header: a3Copy.author.draft, label: row => row.title || a3Copy.author.untitledCoursework, ariaLabel: row => row.title || a3Copy.author.untitledCoursework }}
    columns={[{ id: "state", header: a3Copy.author.state, cell: stateWord },
      { id: "relation", header: "Purpose", cell: draftRelation },
      { id: "saved", header: "Last saved", cell: row => draftSaved(row, timezone, name) }]}
    onOpen={id => host?.visit(`teaching-draft:${courseId}/${id}`, { box })}
    card={row => <div className="grid gap-1"><p dir="auto" className="font-medium">{row.title || a3Copy.author.untitledCoursework}</p><p className="text-meta text-foreground">{stateWord(row)} · {draftRelation(row)}</p><p className="text-meta text-muted-foreground">{draftSaved(row, timezone, name)}</p></div>}
    emptyState={<p>{empty}</p>}
  /></div>;
  return <section aria-label={a3Copy.author.teachingDrafts} className="grid gap-3">
    <h3 className="text-section font-semibold">{a3Copy.author.teachingDrafts}</h3>
    <p className="text-meta text-muted-foreground">{a3Copy.author.unfinishedWritingLeads}</p>
    {active.length ? table(active, a3Copy.author.unfinishedDrafts, a3Copy.author.noDraftsOnThisPage) : <p className="text-ui text-muted-foreground">{a3Copy.author.noUnfinishedDrafts}</p>}
    {history.length > 0 && <details className="grid gap-2">
      <summary className="cursor-pointer text-ui font-medium">{a3Copy.author.draftHistory(history.length)}</summary>
      <p className="text-meta text-muted-foreground">{a3Copy.author.draftHistoryNote}</p>
      {table(history, a3Copy.author.draftHistoryLabel, a3Copy.author.noDraftsOnThisPage)}
    </details>}
    <div className="flex gap-3">{offset > 0 && <Button type="button" variant="outline" onClick={() => setOffset(Math.max(0,offset-100))}>{a3Copy.author.previousDrafts}</Button>}{read.data.next_offset !== null && <Button type="button" variant="outline" onClick={() => setOffset(read.data!.next_offset!)}>{a3Copy.author.nextDrafts}</Button>}</div>
  </section>;
}

/**
 * Other private corrections of one published work, so the team finds a
 * colleague's saved correction without trial-opening same-title drafts (S1A-6).
 */
export function OtherCorrections({ courseId, assignmentId, except, timezone, name, from }: { courseId: string; assignmentId: string; except?: string; timezone: string; name: (id: string) => string | null; from: string }) {
  const host = useContext(BentoVisit);
  const { me } = useAuth();
  const read = useQuery({ queryKey: ["teaching-drafts", courseId, `${me?.tenant_id}:${me?.user_id}`, 0], queryFn: () => teachingDraftsApi.list(courseId, 0), retry: false });
  const others = (read.data?.items ?? []).filter(row => row.assignment_id === assignmentId && row.state === "active" && row.id !== except);
  if (!others.length) return null;
  return <div role="status" className="grid gap-1">
    <p>{others.length === 1 ? "A private correction of this coursework is saved." : `${others.length} private corrections of this coursework are saved.`}</p>
    <ul className="grid gap-1">{others.map(row => <li key={row.id}>{draftRelation(row)} · {draftSaved(row, timezone, name)}{host && <> <Button type="button" variant="link" className="h-auto p-0" onClick={() => host.visit(`teaching-draft:${courseId}/${row.id}`, { box: from })}>Open it</Button></>}</li>)}</ul>
  </div>;
}
